<script setup lang="ts">
import { PropType } from 'vue'

/* Stores */
import { useCityStore } from '~/store/city/city'

/* Components */
import AppButton from '~/components/App/AppButton.vue'
import AppMeasureUnits from '@/components/App/AppMeasureUnits.vue'

/* Types */
import { PropositionItem } from '@/api/modules/basket/types'

const props = defineProps({
  proposition: {
    type: Object as PropType<PropositionItem>,
    required: true,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['setProposition'])

const cityStore = useCityStore()

const { isDesktop, isMobile } = useDevice()

const currency = computed(() => cityStore.currencySymbol)

const isErrorImage = ref(false)

const productImage = computed(() => {
  if (isErrorImage.value || !props.proposition.image_url) {
    return './default-image-desktop.png'
  }

  return props.proposition.image_url
})

const setProposition = () => {
  emit('setProposition', props.proposition)
}

const showTooltip = ref(false)
const toggleTooltip = (toggle: boolean) => {
  showTooltip.value = toggle
}
</script>

<template>
  <div class="proposition" :class="{ 'proposition--mobile': isMobile }">
    <div class="proposition__image-container">
      <nuxt-img
        class="proposition__image"
        :src="productImage"
        alt="изображение добора"
        @error="isErrorImage = true"
      />

      <div v-if="proposition.filters.length > 0" class="proposition__tags">
        <nuxt-img
          v-for="element in proposition.filters"
          :key="element"
          :src="element"
          class="proposition__tag"
          alt="иконка фильтра"
        />
      </div>

      <div class="proposition__discount">{{ proposition.discount }}%</div>
    </div>

    <div class="proposition__title">{{ proposition.name }}</div>

    <div class="proposition__price">
      <div class="proposition__price--new">
        {{ proposition.price }} {{ currency }}
      </div>

      <div class="proposition__price--old">
        {{ proposition.old_price }} {{ currency }}
      </div>
    </div>

    <div class="proposition__description">
      <app-tooltip
        :model-value="showTooltip"
        :location="isDesktop ? 'end' : 'bottom'"
        max-width="200"
      >
        <template #activator>
          <span
            class="proposition__compasition"
            @mouseover="isDesktop ? toggleTooltip(true) : ''"
            @mouseleave="toggleTooltip(false)"
            @click="toggleTooltip(!showTooltip)"
          >
            Состав
          </span>
        </template>
        <template #content>
          <span>
            {{ proposition.marking_composition }}
          </span>
        </template>
      </app-tooltip>

      <app-measure-units
        class="proposition__weight"
        :weight="proposition.weight"
        :volume-in-liters="proposition.volume_in_liters"
        :measure="proposition.measure"
      />
    </div>

    <app-button
      class="proposition__button"
      :disabled="isSelected"
      @click="setProposition"
    >
      <span v-if="isSelected">Выбрано</span>

      <app-icon v-else name="plus-icon" size="40" fill-color="#fff" />
    </app-button>
  </div>
</template>

<style lang="scss" scoped>
.proposition {
  display: grid;
  grid-template-areas:
    'image'
    'title'
    'price'
    'description'
    'button';
  width: 184px;
  padding: 12px;
  color: $text-black;
  background-color: $white;
  border-radius: 16px;
  box-shadow: 0px 6px 30px 0px rgba(50, 52, 72, 0.08);

  &__image-container {
    position: relative;
    display: flex;
    grid-area: image;
  }

  &__image {
    width: 160px;
    height: 108px;
  }

  &__tags {
    position: absolute;
    top: 0;
    left: 0;
    display: grid;
    grid-template-rows: repeat(4, 1fr);
    gap: 10px;
  }

  &__tag {
    width: 20px;
    height: 20px;
  }

  &__discount {
    position: absolute;
    top: 0;
    right: 0;
    padding: 3px 6px;
    font-size: 9px;
    font-weight: 800;
    line-height: 140%;
    color: #000;
    letter-spacing: 0.45px;
    background-color: $discount-yellow;
    border-radius: 20px;
  }

  &__title {
    display: -webkit-box;
    grid-area: title;
    height: 60px;
    margin-top: 16px;
    overflow: hidden;
    font-size: 16px;
    font-weight: 800;
    line-height: 125%;
    text-overflow: ellipsis;
    white-space: initial;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }

  &__price {
    display: flex;
    grid-area: price;
    gap: 6px;
    align-items: baseline;

    &--new {
      font-size: 22px;
      font-weight: 800;
      line-height: normal;
    }

    &--old {
      font-size: 16px;
      font-weight: 600;
      line-height: normal;
      color: $dark-grey;
      text-decoration: line-through;
    }
  }

  &__description {
    display: flex;
    grid-area: description;
    gap: 8px;
    align-items: center;
    margin-top: 17px;
  }

  &__compasition {
    font-size: 14px;
    font-weight: 600;
    line-height: 120%;
    color: $primary;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  &__weight {
    font-size: 14px;
    font-weight: 600;
    color: $dark-grey;
  }

  &__button {
    grid-area: button;
    width: 100%;
    margin-top: 16px;
    font-weight: 600;
    border-radius: 100px !important;

    :deep(.v-btn__content) {
      font-weight: 600;
    }

    &.v-btn.v-btn--disabled {
      color: $text-dark-gray !important;
      background: #f5f6f7 !important;
      box-shadow: none !important;
      opacity: unset;
    }
  }

  &--mobile {
    grid-template-areas:
      'image'
      'price'
      'title'
      'description'
      'button';
    grid-template-rows: repeat(5, max-content);
    width: 144px;
    height: 100%;

    .proposition__image {
      width: 120px;
      height: 78px;
    }

    .proposition__tags {
      gap: 8px;
    }

    .proposition__tag {
      width: 14px;
      height: 14px;
    }

    .proposition__price {
      margin-top: 15px;

      &--new {
        font-size: 14px;
      }

      &--old {
        font-size: 12px;
      }
    }

    .proposition__title {
      height: unset;
      margin-top: 4px;
      font-size: 12px;
      font-weight: 600;
    }

    .proposition__description {
      margin: 8px 0 16px;
    }

    .proposition__compasition {
      font-size: 10px;
    }

    .proposition__weight {
      font-size: 10px;
    }

    .proposition__button {
      grid-row: none;
      margin-top: auto;
    }
  }
}
</style>
